import { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery } from 'react-query';
import HouseholdService from '../../services/householdService';

export const useHouseholdDetails = () => {
  const [currentParams, setCurrentParams] = useState(null);
  const [currentTab, setCurrentTab] = useState('approved');
  const [page, setPage] = useState(0);
  const accumulatedData = useRef({ households: [], totalCount: 0 });

  const householdService = useMemo(() => new HouseholdService(), []);
  const limit = 50;

  const { isLoading, error: queryError } = useQuery(
    [
      'gross-lease-households',
      currentParams?.organizationId,
      currentParams?.propertyId,
      currentTab,
      page,
    ],
    async () => {
      if (!currentParams) return null;

      return householdService.getGrossLeaseHouseholds(
        currentParams.organizationId,
        currentParams.propertyId,
        currentTab,
        limit,
        page * limit,
      );
    },
    {
      enabled: !!currentParams,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          if (page === 0) {
            // First page - replace data
            accumulatedData.current = {
              households: data.households,
              totalCount: data.totalCount,
            };
          } else {
            // Subsequent pages - append data
            accumulatedData.current = {
              households: [
                ...accumulatedData.current.households,
                ...data.households,
              ],
              totalCount: data.totalCount,
            };
          }
        }
      },
    },
  );

  const fetchHouseholds = useCallback(
    (organizationId, propertyId, initialTab = 'approved') => {
      setCurrentParams({ organizationId, propertyId });
      setCurrentTab(initialTab);
      setPage(0);
      // Reset accumulated data when fetching new households
      accumulatedData.current = { households: [], totalCount: 0 };
    },
    [],
  );

  const switchTab = useCallback((newTab) => {
    setCurrentTab(newTab); // This changes the query key → new fetch
    setPage(0); // Reset to first page
    // Reset accumulated data when switching tabs
    accumulatedData.current = { households: [], totalCount: 0 };
  }, []);

  const loadMore = useCallback(() => {
    if (
      accumulatedData.current &&
      accumulatedData.current.households.length <
        accumulatedData.current.totalCount &&
      !isLoading
    ) {
      setPage((prev) => prev + 1);
    }
  }, [isLoading]);

  const hasMore = accumulatedData.current
    ? accumulatedData.current.households.length <
      accumulatedData.current.totalCount
    : false;

  return {
    data:
      accumulatedData.current.households.length > 0
        ? accumulatedData.current
        : null,
    loading: isLoading,
    error: queryError?.message || null,
    fetchHouseholds,
    switchTab,
    loadMore,
    hasMore,
    currentTab,
  };
};
